import { motion, AnimatePresence } from "framer-motion";
import { LucideUploadCloud, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { http } from "../../axios/axios";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";

// const baseURL =import.meta.env.VITE_API_BASE || "https://api.suraksha24x7.com";

const API_BASE = "http://localhost:8080";

const getImageUrl = (path) => {
  if (!path) return "";

  if (path.startsWith("blob:")) return path;

  if (path.startsWith("http")) return path;

  const cleanPath = path.replace(/\\/g, "/");

  return `${API_BASE.replace(/\/$/, "")}/${cleanPath.replace(/^\//, "")}`;
};

function CreateClientModal({
  closeModal,
  handleSubmit,
  handleImageChange,
  previewImage,
  removeImage,
}) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 backdrop-blur-xl z-40"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ duration: 0.2 }}
        className="
        fixed z-50
        top-1/2 left-1/2
        -translate-x-1/2 -translate-y-1/2
        w-[92%] sm:w-[500px]
        max-h-[90vh]
        bg-[#0a0f1a]/95
        border border-white/10
        rounded-[2rem]
        shadow-2xl
        overflow-hidden
      "
      >
        <div
          className="
        px-8 py-6
        flex items-center justify-between
        border-b border-white/10
        bg-white/[0.03]
      "
        >
          <div>
            <h3 className="text-lg font-black text-white uppercase">
              Client Creation
            </h3>

            <p className="text-xs text-slate-400 mt-1">
              Upload client logo/image
            </p>
          </div>

          <button
            type="button"
            onClick={closeModal}
            className="
            h-10 w-10
            rounded-xl
            bg-white/5
            hover:bg-red-500
            text-slate-400
            hover:text-white
            transition-all
            flex items-center justify-center
          "
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="
          p-8
          space-y-6
          overflow-y-auto
        "
        >
          <label
            className="
            relative
            h-64
            w-full
            flex items-center justify-center
            rounded-2xl
            border-2 border-dashed border-white/10
            bg-slate-900/60
            hover:border-blue-500
            transition-all
            cursor-pointer
            overflow-hidden
          "
          >
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />

            {previewImage ? (
              <div className="relative w-full h-full group">
                <img
                  src={getImageUrl(previewImage)}
                  loading="lazy"
                  alt="preview"
                  className="
                  w-full h-full
                  object-contain
                  group-hover:scale-105
                  transition-transform
                "
                />

                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeImage();
                  }}
                  className="
                  absolute top-3 right-3
                  bg-black/70
                  hover:bg-red-600
                  p-2
                  rounded-xl
                  text-white
                  transition-all
                "
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div
                  className="
                h-14 w-14
                rounded-2xl
                bg-blue-600/20
                flex items-center justify-center
              "
                >
                  <LucideUploadCloud size={28} className="text-blue-400" />
                </div>

                <span className="text-sm text-slate-400 mt-3">
                  Click to upload image
                </span>
              </div>
            )}
          </label>

          <button
            type="submit"
            className="
            w-full
            bg-blue-600
            hover:bg-blue-500
            py-4
            rounded-xl
            font-bold
            text-white
            transition-all
            shadow-lg
            shadow-blue-600/20
          "
          >
            Publish Client
          </button>
        </form>
      </motion.div>
    </>
  );
}

const AdminClientPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [clients, setClients] = useState([]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    if (previewImage) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Purge this client from the database?")) return;
    try {
      await http.delete(`/clients/${id}`);
      toast.success("Client Purged");
      getClients();
    } catch (error) {
      toast.error("Purge Failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await http.post(`clients`, formData);
      toast.success("Client Created Successfully");
      setIsModalOpen(false);
      setPreviewImage("");
      getClients();
    } catch (error) {}
  };

  const getClients = async () => {
    try {
      const response = await http.get(`/clients`);
      setClients(response.data?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getClients();
  }, []);

  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);
  return (
    <div className="p-4 md:p-8 min-h-screen text-slate-200">
      <AdminHeader
        title="Clients Section"
        buttonText="Create Client"
        onClick={() => setIsModalOpen(!isModalOpen)}
      />


      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-2">
        {clients.map((client) => (
          <div
            key={client._id}
            className="
    group
    h-40
    bg-slate-900
    border
    border-blue-600
    rounded-2xl
    overflow-hidden
    relative
    shadow-lg
    hover:shadow-blue-500/20
    hover:-translate-y-1
    transition-all
    duration-300
  "
          >
            <img
              src={getImageUrl(client.image)}
              loading="lazy"
              alt="client"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />

            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />

            <button
              onClick={() => handleDelete(client._id)}
              className="absolute top-3 right-3 bg-black/70  p-2 rounded-xl 
    hover:bg-red-600 transition-all duration-200 cursor-pointer"
            >
              <Trash2
                size={18}
                className="text-red-400 group-hover:text-white transition-all"
              />
            </button>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <CreateClientModal
            handleSubmit={handleSubmit}
            closeModal={() => {
              setIsModalOpen(false);
              setPreviewImage("");
            }}
            handleImageChange={handleImageChange}
            previewImage={previewImage}
            removeImage={removeImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminClientPage;
